/**
 * Ccontactformstep Component
 * Reusable form step for contact form with text input and navigation buttons
 *
 * Animation timing:
 * - Buttons fade in AFTER the parent swipe animation completes (200ms delay)
 * - Buttons fade out quickly at the START of exit (100ms, no delay)
 */
'use client';
import { useCallback } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { IconChevronLeft } from '@tabler/icons-react';
import { Ccontainer } from '@/components/ui/Ccontainer';
import { BUTTON_ICON_POSITION, Cbutton } from '@/components/ui/Cbutton';
import { Ctextinput } from '@/components/ui/Ctextinput';
import { ButtonPreset } from '@/constants/componentPresets';
// Animation duration constants (should match Canimatedswitch default duration)
const SWIPE_DURATION_MS = 200;
const BUTTON_FADE_DURATION_MS = 150;
const TEXT_ALIGN_CENTER = 'center';
export interface CcontactformstepProps {
    /** Placeholder text for the input */
    placeholder: string;
    /** Current value of the input */
    value: string;
    /** Callback when input value changes */
    onChange: (value: string) => void;
    /** Text for the proceed/submit button */
    buttonText: string;
    /** Callback when proceed/submit button is clicked */
    onProceed: () => void;
    /** Callback when back button is clicked */
    onBack: () => void;
    /** Number of rows for multiline input (default: 1) */
    rows?: number;
    /** Show loading state on proceed button */
    isLoading?: boolean;
    /** Show error styling on input (danger border) */
    isError?: boolean;
    /** Disable the proceed button */
    isDisabled?: boolean;
    /** Additional className */
    className?: string;
    /** Additional style */
    style?: CSSProperties;
}
export function Ccontactformstep({ placeholder, value, onChange, buttonText, onProceed, onBack, rows = 1, isLoading = false, isError = false, isDisabled = false, className, style, }: CcontactformstepProps) {
    const stepIdentity = `${placeholder}-${buttonText}`;
    // Handle Enter key to trigger proceed (only for single-line inputs)
    // For multi-line (rows > 1), Enter creates new lines, so we don't intercept
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // Only handle Enter for single-line inputs
        if (rows > 1)
            return;
        if (e.key === 'Enter' && !isDisabled && !isLoading) {
            e.preventDefault();
            onProceed();
        }
    }, [rows, isDisabled, isLoading, onProceed]);
    return <Ccontainer rootAttributes={{ 'data-el': 'Ccontactformstep/Root' }} display="flex" flexDirection="column" alignItems="center" justifyContent="center" className={className} style={style} contentWrapperStyle={{ gap: '20px', width: 400 }}>
            <Ctextinput dataElScope="Ccontactformstep/Form" value={value} onChange={onChange} placeholder={placeholder} textAlign={TEXT_ALIGN_CENTER} fontSize={20} height={rows > 1 ? undefined : 110} radius={32} rows={rows} isError={isError} autoFocus onKeyDown={handleKeyDown}/>
            {/* Buttons with fade animation:
        - Enter: fade in AFTER swipe completes (delayed)
        - Exit: fade out quickly at the START of swipe */}
            <motion.div initial={{ opacity: 0 }} animate={{
            opacity: 1,
            transition: {
                duration: BUTTON_FADE_DURATION_MS / 1000,
                delay: SWIPE_DURATION_MS / 1000,
                ease: [0.4, 0, 0.2, 1],
            },
        }} exit={{
            opacity: 0,
            transition: {
                duration: BUTTON_FADE_DURATION_MS / 1000,
                delay: 0,
                ease: [0.4, 0, 0.2, 1],
            },
        }}>
                <Ccontainer display="flex" flexDirection="row" alignItems="center" justifyContent="center" contentWrapperStyle={{ gap: '20px', marginRight: 39 }}>
                    <Cbutton rootAttributes={{ 'data-el': 'Ccontactformstep/BackTrigger' }} preset={ButtonPreset.Secondary} icon={IconChevronLeft} iconPosition={BUTTON_ICON_POSITION.Left} onClick={onBack}/>
                    <Cbutton rootAttributes={{ 'data-el': 'Ccontactformstep/ProceedTrigger' }} preset={ButtonPreset.Secondary} label={buttonText} onClick={onProceed} isLoading={isLoading} disabled={isDisabled}/>
                </Ccontainer>
            </motion.div>
        </Ccontainer>
        ;
}
